import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { CardMainLayout, Catcher, FieldInfoAdvanced, LinkComponent } from '../../';
import { IInvoice, NotificationType } from '../../../interfaces';
import { useBillingCompaniesSelect, useCompanyInfo } from '../../../hooks';
import { addNotification, getBillingCompaniesSelect, getLastInvoice } from '../../../middleware';

export const ShowCompanyInvoicing: React.FC = observer(() => {
    const companyInfo = useCompanyInfo();
    const [invoiceNumber, setInvoiceNumber] = useState<IInvoice | void>();
    const select = useBillingCompaniesSelect();
    const invoicingCompany = select.find((value) => value.id === companyInfo?.invoicedByCompanyId);

    const getLastInvoiceLink = async () => {
        const invoice = await getLastInvoice({ sellerId: companyInfo?.id, pageNumber: 1, pageSize: 1 });

        if (!invoice) {
            addNotification({ type: NotificationType.ERROR, message: 'Error fetching invoice number' });
        }

        setInvoiceNumber(invoice);
    };

    useEffect(() => {
        if (companyInfo.companyCode) {
            getLastInvoiceLink();
        } else if (companyInfo?.invoicedByCompanyId) {
            getBillingCompaniesSelect({ isInvoicing: true }).then();
        }
    }, [companyInfo.companyCode, companyInfo?.invoicedByCompanyId]);

    return (
        <Catcher>
            <CardMainLayout
                idSpinners={['GET_COMPANY', 'GET_COMPANY_ABOUT']}
                name={'Company Invoicing'}
                dataTest={'ShowCompany'}
            >
                {companyInfo.companyCode ? (
                    <>
                        <FieldInfoAdvanced title={'Company Code'}>{companyInfo.companyCode}</FieldInfoAdvanced>
                        <FieldInfoAdvanced title={'Last invoice created'}>
                            {invoiceNumber ? (
                                <LinkComponent
                                    currentListComponent={'InvoicesList'}
                                    id={`${invoiceNumber?.sellerId}`}
                                    name={invoiceNumber?.seller}
                                />
                            ) : null}
                        </FieldInfoAdvanced>
                        <FieldInfoAdvanced title={'Address'}>
                            {companyInfo?.addressStreet}, {companyInfo?.addressNumber}, {companyInfo?.addressBuilding}
                        </FieldInfoAdvanced>
                        <FieldInfoAdvanced title={'Vat number'}>{companyInfo?.vatNumber}</FieldInfoAdvanced>

                        {companyInfo?.iban && <FieldInfoAdvanced title={'IBAN'}>{companyInfo.iban}</FieldInfoAdvanced>}

                        {companyInfo?.swift && (
                            <FieldInfoAdvanced title={'SWIFT'}>{companyInfo.swift}</FieldInfoAdvanced>
                        )}

                        {companyInfo?.accountNumber && (
                            <FieldInfoAdvanced title={'Account Number'}>{companyInfo.accountNumber}</FieldInfoAdvanced>
                        )}

                        {companyInfo?.invoiceFooter && (
                            <FieldInfoAdvanced title={'Invoice Footer'}>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{companyInfo.invoiceFooter}</div>
                            </FieldInfoAdvanced>
                        )}
                    </>
                ) : (
                    <FieldInfoAdvanced title={'Invoicing Company'}>
                        {invoicingCompany || companyInfo?.invoicedByCompanyId ? (
                            <LinkComponent
                                currentListComponent={'BillingCompaniesList'}
                                id={`${companyInfo?.invoicedByCompanyId}`}
                                name={invoicingCompany?.name || companyInfo?.invoicedByCompanyId}
                            />
                        ) : (
                            'None'
                        )}
                    </FieldInfoAdvanced>
                )}
            </CardMainLayout>
        </Catcher>
    );
});
